/** Things in the world that are not weapons: ice, water, the drop, pods, captives, machines. */

import type { Layer } from '../synth/patch';
import { PRIORITY, type SfxDef } from './types';
import { syllable } from './voice';

const SPLASH: Layer[] = [
  { wave: 'noise', attack: 0.02, decay: 0.5, gain: 1, filter: { type: 'bp', cutoff: 1300, cutoffEnd: 480, q: 0.9 } },
  { wave: 'noise', attack: 0.004, decay: 0.12, curve: 2.5, gain: 0.7, filter: { type: 'hp', cutoff: 2600 } },
  { wave: 'sine', freq: 110, freqEnd: 50, decay: 0.2, gain: 0.7 },
  { wave: 'sine', at: 0.18, freq: 420, freqEnd: 980, attack: 0.005, decay: 0.06, gain: 0.3 },
  { wave: 'sine', at: 0.27, freq: 560, freqEnd: 1300, attack: 0.005, decay: 0.05, gain: 0.25 },
  { wave: 'sine', at: 0.39, freq: 380, freqEnd: 820, attack: 0.005, decay: 0.07, gain: 0.2 },
];

export const FIELD_SFX = {
  /**
   * Thunder (02 §5), a few seconds behind its stroke. Not a bang: a crack that arrives already
   * spread out by the miles it crossed, then a long rumble that rolls away and comes back. Two
   * variants and a wide pitch jitter, so the same storm never repeats itself.
   */
  thunder: {
    variants: 2, priority: PRIORITY.ambient, gain: 0.55, maxDistance: 400, rateSpread: 0.18,
    patch: {
      layers: [
        // The crack, already softened - a near strike would be a slap, and this one was not near.
        { wave: 'noise', attack: 0.012, hold: 0.05, decay: 0.5, curve: 2, gain: 0.85,
          filter: { type: 'lp', cutoff: 2600, cutoffEnd: 300 } },
        // The body of it: low, slow to arrive, slower to go.
        { wave: 'noise', at: 0.05, attack: 0.22, hold: 0.6, decay: 2.4, curve: 1.6, gain: 1,
          filter: { type: 'lp', cutoff: 420, cutoffEnd: 90 } },
        // The roll - a second swell behind the first, which is what makes it thunder and not a bang.
        // Ends inside the bank's 4 s ceiling for a one-shot; the distance is sold by the delay
        // before it arrives (`ambience.ts`), not by making the buffer longer.
        { wave: 'noise', at: 0.85, attack: 0.45, hold: 0.45, decay: 2.05, curve: 1.4, gain: 0.55,
          tremHz: 1.6, tremDepth: 0.35, filter: { type: 'lp', cutoff: 260, cutoffEnd: 70 } },
        // The floor you feel rather than hear.
        { wave: 'sine', at: 0.04, freq: 44, freqEnd: 26, attack: 0.15, hold: 0.5, decay: 1.8, gain: 0.5 },
      ],
      jitter: { pitch: 0.12, gain: 0.2, time: 0.05 },
    },
  },
  /** A crack like a rifle shot, the sheet groaning, then the man going through (01 §10). */
  iceBreak: {
    variants: 2, priority: PRIORITY.critical, gain: 0.9, maxDistance: 90,
    patch: {
      layers: [
        { wave: 'noise', decay: 0.035, curve: 3, gain: 1, filter: { type: 'bp', cutoff: 4200, q: 3 } },
        { wave: 'sine', freq: 3100, fmRatio: 1.37, fmIndex: 3, fmDecay: 0.015, decay: 0.09, curve: 3, gain: 0.7 },
        { wave: 'saw', at: 0.02, freq: 96, freqEnd: 52, vibHz: 9, vibDepth: 1.2, attack: 0.02, decay: 0.32, gain: 0.6,
          filter: { type: 'lp', cutoff: 600 } },
        ...SPLASH.map((l) => ({ ...l, at: (l.at ?? 0) + 0.16 })),
      ],
      jitter: { pitch: 0.06, time: 0.06 },
    },
  },
  splash: {
    variants: 3, priority: PRIORITY.action, gain: 0.7, maxDistance: 70, rateSpread: 0.06,
    patch: { layers: SPLASH.map((l) => ({ ...l })), jitter: { pitch: 0.1, time: 0.1 } },
  },
  /** The jump light's buzzer: harsh, unmistakable, and the last thing heard aboard (01 §10). */
  jumpBuzzer: {
    variants: 1, priority: PRIORITY.critical, gain: 0.4,
    patch: {
      layers: [
        { wave: 'square', freq: 466, attack: 0.004, hold: 0.85, decay: 0.05, gain: 1, tremHz: 58, tremDepth: 0.7,
          tremSquare: true, crush: 9000, filter: { type: 'lp', cutoff: 3400 } },
        { wave: 'saw', freq: 233.5, attack: 0.004, hold: 0.85, decay: 0.05, gain: 0.5,
          filter: { type: 'lp', cutoff: 1800 } },
      ],
      drive: 1.5,
    },
  },
  /** Mission 11's start signal: two short pips and a long one, pure square wave, as in 1987. */
  signalBeep: {
    variants: 1, priority: PRIORITY.critical, gain: 0.3,
    patch: {
      layers: [
        { wave: 'square', freq: 1318.5, attack: 0.003, hold: 0.11, decay: 0.015, gain: 1, crush: 11025 },
        { wave: 'square', at: 0.22, freq: 1318.5, attack: 0.003, hold: 0.11, decay: 0.015, gain: 1, crush: 11025 },
        { wave: 'square', at: 0.44, freq: 1760, attack: 0.003, hold: 0.55, decay: 0.03, gain: 1, crush: 11025 },
      ],
    },
  },
  chuteOpen: {
    variants: 1, priority: PRIORITY.critical, gain: 0.8,
    patch: {
      layers: [
        { wave: 'noise', attack: 0.03, decay: 0.5, gain: 1, filter: { type: 'lp', cutoff: 950, cutoffEnd: 150 } },
        { wave: 'sine', freq: 124, freqEnd: 48, decay: 0.26, gain: 0.9 },
        { wave: 'noise', at: 0.3, attack: 0.1, decay: 1.3, gain: 0.3, tremHz: 13, tremDepth: 0.9,
          filter: { type: 'bp', cutoff: 1400, q: 0.9 } },
      ],
    },
  },
  landThump: {
    variants: 2, priority: PRIORITY.critical, gain: 0.8,
    patch: {
      layers: [
        { wave: 'sine', freq: 112, freqEnd: 38, sweep: 0.6, decay: 0.24, gain: 1 },
        { wave: 'noise', decay: 0.19, curve: 2.5, gain: 0.9, filter: { type: 'lp', cutoff: 1000, cutoffEnd: 200 } },
        { wave: 'sine', at: 0.06, freq: 1500, fmRatio: 2.2, fmIndex: 2, fmDecay: 0.02, decay: 0.05, curve: 3,
          gain: 0.3 },
        { wave: 'pink', at: 0.1, attack: 0.05, decay: 0.4, gain: 0.35, filter: { type: 'bp', cutoff: 1500, q: 0.8 } },
      ],
      jitter: { pitch: 0.05 },
    },
  },
  wireSnag: {
    variants: 2, priority: PRIORITY.action, gain: 0.55,
    patch: {
      layers: [
        { wave: 'saw', freq: 322, freqEnd: 300, fmRatio: 3.1, fmIndex: 1.5, fmDecay: 0.2, decay: 0.5, gain: 1,
          filter: { type: 'bp', cutoff: 1800, q: 5 } },
        { wave: 'noise', attack: 0.02, decay: 0.3, gain: 0.5, tremHz: 35, tremDepth: 0.8,
          filter: { type: 'hp', cutoff: 3500 } },
      ],
      jitter: { pitch: 0.08 },
    },
  },
  podDropped: {
    variants: 1, priority: PRIORITY.action, gain: 0.5,
    patch: {
      layers: [
        { wave: 'sine', freq: 180, freqEnd: 110, decay: 0.08, gain: 1 },
        { wave: 'noise', decay: 0.05, curve: 3, gain: 0.8, filter: { type: 'bp', cutoff: 1200, q: 2 } },
        { wave: 'noise', at: 0.06, attack: 0.08, decay: 0.7, gain: 0.6,
          filter: { type: 'bp', cutoff: 1700, cutoffEnd: 450, q: 1.5 } },
      ],
    },
  },
  podLanded: {
    variants: 2, priority: PRIORITY.cue, gain: 0.7, maxDistance: 120,
    patch: {
      layers: [
        { wave: 'sine', freq: 96, freqEnd: 40, sweep: 0.6, decay: 0.22, gain: 1 },
        { wave: 'noise', decay: 0.16, curve: 2.5, gain: 0.8, filter: { type: 'lp', cutoff: 750 } },
        { wave: 'sine', at: 0.03, freq: 820, fmRatio: 1.48, fmIndex: 2.5, fmDecay: 0.04, decay: 0.12, curve: 3,
          gain: 0.35 },
      ],
      jitter: { pitch: 0.06 },
    },
  },
  podSmashed: {
    variants: 2, priority: PRIORITY.cue, gain: 0.75, maxDistance: 120,
    patch: {
      layers: [
        { wave: 'sine', freq: 100, freqEnd: 36, sweep: 0.6, decay: 0.25, gain: 1 },
        { wave: 'noise', attack: 0.004, decay: 0.42, gain: 1, tremHz: 41, tremDepth: 0.85,
          filter: { type: 'bp', cutoff: 1900, q: 1 } },
        { wave: 'sine', at: 0.05, freq: 1300, fmRatio: 1.43, fmIndex: 3.2, fmDecay: 0.05, decay: 0.16, curve: 3,
          gain: 0.5 },
        { wave: 'sine', at: 0.21, freq: 760, fmRatio: 1.43, fmIndex: 3.2, fmDecay: 0.05, decay: 0.14, curve: 3,
          gain: 0.4 },
      ],
      jitter: { pitch: 0.06 },
    },
  },
  podOpened: {
    variants: 1, priority: PRIORITY.action, gain: 0.5,
    patch: {
      layers: [
        { wave: 'sine', freq: 1400, fmRatio: 2.4, fmIndex: 2, fmDecay: 0.012, decay: 0.04, curve: 3, gain: 0.8 },
        { wave: 'sine', at: 0.14, freq: 1250, fmRatio: 2.4, fmIndex: 2, fmDecay: 0.012, decay: 0.04, curve: 3,
          gain: 0.8 },
        { wave: 'saw', at: 0.26, freq: 182, freqEnd: 266, vibHz: 14, vibDepth: 0.5, attack: 0.04, decay: 0.34,
          gain: 0.5, filter: { type: 'bp', cutoff: 900, q: 4 } },
        { wave: 'sine', at: 0.62, freq: 140, freqEnd: 80, decay: 0.1, gain: 1 },
        { wave: 'noise', at: 0.62, decay: 0.05, curve: 3, gain: 0.6, filter: { type: 'lp', cutoff: 1100 } },
      ],
    },
  },
  /** An NCO's pea whistle: reinforcements are coming (07 §3). */
  whistle: {
    variants: 2, priority: PRIORITY.cue, gain: 0.45, maxDistance: 140,
    patch: {
      layers: [
        { wave: 'sine', freq: 2350, freqEnd: 2420, attack: 0.015, hold: 0.28, decay: 0.06, gain: 1, tremHz: 38,
          tremDepth: 0.55 },
        { wave: 'noise', attack: 0.015, hold: 0.28, decay: 0.06, gain: 0.35,
          filter: { type: 'bp', cutoff: 2380, q: 8 } },
        { wave: 'sine', at: 0.45, freq: 2350, freqEnd: 2300, attack: 0.015, hold: 0.5, decay: 0.1, gain: 1, tremHz: 38,
          tremDepth: 0.55 },
        { wave: 'noise', at: 0.45, attack: 0.015, hold: 0.5, decay: 0.1, gain: 0.35,
          filter: { type: 'bp', cutoff: 2340, q: 8 } },
      ],
      jitter: { pitch: 0.03 },
    },
  },
  captiveFreed: {
    variants: 1, priority: PRIORITY.action, gain: 0.5,
    patch: {
      layers: [
        { wave: 'sine', freq: 1900, fmRatio: 2.63, fmIndex: 2, fmDecay: 0.02, decay: 0.07, curve: 3, gain: 0.7 },
        { wave: 'sine', at: 0.09, freq: 1500, fmRatio: 2.63, fmIndex: 2, fmDecay: 0.02, decay: 0.08, curve: 3,
          gain: 0.7 },
        { wave: 'noise', at: 0.05, decay: 0.2, gain: 0.4, tremHz: 33, tremDepth: 0.9,
          filter: { type: 'bp', cutoff: 2600, q: 2 } },
        ...syllable({ at: 0.3, dur: 0.26, from: 170, to: 215, vowel: 'oh', breath: 0.4, gain: 0.7 }),
      ],
    },
  },
  captiveLost: {
    variants: 1, priority: PRIORITY.critical, gain: 0.4,
    patch: {
      layers: [
        { wave: 'triangle', freq: 440, attack: 0.02, hold: 0.16, decay: 0.1, gain: 1 },
        { wave: 'triangle', at: 0.26, freq: 349.23, freqEnd: 340, attack: 0.02, hold: 0.2, decay: 0.5, gain: 1 },
      ],
    },
  },
  /** A man giving up: a falling two-syllable "uh-oh" and his weapon hitting the ground. */
  surrender: {
    variants: 1, priority: PRIORITY.action, gain: 0.55,
    patch: {
      layers: [
        ...syllable({ at: 0, dur: 0.14, from: 230, to: 220, vowel: 'uh' }),
        ...syllable({ at: 0.18, dur: 0.26, from: 185, to: 140, vowel: 'oh' }),
        { wave: 'sine', at: 0.5, freq: 1100, fmRatio: 1.6, fmIndex: 2.6, fmDecay: 0.03, decay: 0.1, curve: 3,
          gain: 0.6 },
        { wave: 'noise', at: 0.5, decay: 0.08, curve: 2.5, gain: 0.6, filter: { type: 'bp', cutoff: 900, q: 1.5 } },
      ],
    },
  },
  /** A minitank's track links, played once per 0.4 m so the clatter follows its speed. */
  tread: {
    variants: 4, priority: PRIORITY.cue, gain: 0.4, rateSpread: 0.05, minInterval: 0.02, maxDistance: 80,
    patch: {
      layers: [
        { wave: 'sine', freq: 640, fmRatio: 1.47, fmIndex: 2.8, fmDecay: 0.015, decay: 0.05, curve: 3, gain: 0.8 },
        { wave: 'noise', decay: 0.04, curve: 2.5, gain: 1, filter: { type: 'bp', cutoff: 1500, q: 1.5 } },
        { wave: 'saw', freq: 58, decay: 0.09, gain: 0.8, filter: { type: 'lp', cutoff: 300 } },
      ],
      jitter: { pitch: 0.08, gain: 0.2 },
    },
  },
} satisfies Record<string, SfxDef>;
